import { ChatLaunch, ChevronDown, ChevronUp } from '@carbon/icons-react';
import classNames from 'classnames';
import * as React from 'react';

import { VoiceboxChatSuggestedPromptsBadges } from 'bones/components/molecules/Voicebox/chat/VoiceboxChatSuggestedPromptsBadges';
import { useVoiceboxConversation } from 'bones/components/molecules/Voicebox/context/VoiceboxConversation';
import { useVoiceboxSchema } from 'bones/components/molecules/Voicebox/context/VoiceboxSchema';
import { useVoiceboxSettings } from 'bones/components/molecules/Voicebox/context/VoiceboxSettings';
import { Button } from 'bones/design-system/components/Button';
import { Icon } from 'bones/design-system/components/icons/Icon';
import { VoiceboxLogo } from 'bones/design-system/components/icons/VoiceboxLogo';
import { Heading } from 'bones/design-system/components/typography/Heading';
import { useDidValueChange } from 'bones/utils/hooks/useDidValueChange';
import { isEqual } from 'bones/utils/isEqual';

export interface VoiceboxChatSuggestedPromptsProps {
  children?: React.ReactNode;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  sendMessage: (message: string) => void;
  sendMessageDisabled: boolean;
}

export const VoiceboxChatSuggestedPrompts: React.VFC<
  VoiceboxChatSuggestedPromptsProps
> = ({ children, sendMessage, sendMessageDisabled, isOpen, setIsOpen }) => {
  const { config, copy, testIds } = useVoiceboxSchema();
  const { isLoadingConversation, messages } = useVoiceboxConversation();
  const { databasesById, settings } = useVoiceboxSettings();

  const examples =
    config.examples || databasesById[settings.databaseId]?.examples || [];

  const [isExpanded, setIsExpanded] = React.useState(false);

  const listRef = React.useRef<HTMLDivElement>(null);

  const didExamplesChange = useDidValueChange(examples, isEqual);
  const didLoadingConversationChange = useDidValueChange(isLoadingConversation);
  const hasExamples = examples.length > 1;
  const hasMessages = Boolean(messages.length);
  React.useEffect(() => {
    if (
      (didExamplesChange || didLoadingConversationChange) &&
      !isLoadingConversation &&
      !hasMessages &&
      hasExamples
    ) {
      setIsOpen(true);
    }
  }, [
    didExamplesChange,
    didLoadingConversationChange,
    hasExamples,
    hasMessages,
    isLoadingConversation,
    setIsOpen,
  ]);

  const handleExpand = (nextIsExpanded: boolean) => {
    if (!nextIsExpanded && listRef.current) {
      listRef.current.scrollTop = 0;
    }
    setIsExpanded(nextIsExpanded);
  };

  return (
    <div className="vb-voicebox-chat-suggested-prompts">
      <div
        className={classNames('vb-voicebox-chat-suggested-prompts-transition', {
          'vb-voicebox-chat-suggested-prompts-transition--collapsed': !isOpen,
        })}
      >
        <div className="vb-voicebox-chat-suggested-prompts-popover">
          <div className="vb-voicebox-chat-suggested-prompts-header">
            <div className="vb-voicebox-chat-suggested-prompts-title">
              <Icon IconComponent={ChatLaunch} size="md" />
              <Heading size="md">{copy.message.example.title}</Heading>
            </div>
            <Button
              onClick={() => setIsOpen(false)}
              size="xs"
              tabIndex={isOpen ? undefined : -1}
              testId={testIds.messages.example.close}
              variant="ghost"
            >
              {copy.message.example.close}
            </Button>
          </div>

          <div
            ref={listRef}
            className={classNames('vb-voicebox-chat-suggested-prompts-list', {
              'vb-voicebox-chat-suggested-prompts-list--collapsed': !isExpanded,
            })}
          >
            {examples.map((example, exampleIdx) => (
              <Button
                key={example}
                leftIcon={VoiceboxLogo}
                onClick={
                  sendMessageDisabled
                    ? undefined
                    : () => {
                        setIsOpen(false);
                        sendMessage(example);
                      }
                }
                size="md"
                tabIndex={
                  isOpen && (isExpanded || exampleIdx < 3) ? undefined : -1
                }
                testId={testIds.messages.example.button(example, exampleIdx)}
                variant="ghost2-gray"
              >
                {example}
              </Button>
            ))}
          </div>

          {examples.length > 3 ? (
            <Button
              leftIcon={isExpanded ? ChevronUp : ChevronDown}
              onClick={() => handleExpand(!isExpanded)}
              size="xs"
              tabIndex={isOpen ? undefined : -1}
              testId={
                isExpanded
                  ? testIds.messages.example.less
                  : testIds.messages.example.more
              }
              variant="ghost-gray"
            >
              {isExpanded
                ? copy.message.example.less
                : copy.message.example.more}
            </Button>
          ) : null}
        </div>
      </div>

      <VoiceboxChatSuggestedPromptsBadges
        isOpen={isOpen}
        sendMessage={sendMessage}
        sendMessageDisabled={sendMessageDisabled}
        setIsOpen={setIsOpen}
      />

      {children}
    </div>
  );
};
